import { FunctionComponent, useEffect, useState } from 'react';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, Typography } from '@mui/material';

type QuantityCounterProps = {
    onCounterChange: (next: number, action?: 'add' | 'subtract') => void;
    initial?: number;
    allowNegative?: boolean;
    min?: number;
    max?: number;
    value?: number;
    disabled?: boolean;
};

export const QuantityCounter: FunctionComponent<QuantityCounterProps> = ({
    initial = 0,
    allowNegative = true,
    onCounterChange,
    min = null,
    max = null,
    value = null,
    disabled,
}) => {
    const [counter, setCounter] = useState(value || initial);
    const [disableDown, setDisableDown] = useState(false);
    const [disableUp, setDisableUp] = useState(false);

    const onChange = (action: 'add' | 'subtract') => () => {
        let nextValue = action === 'add' ? counter + 1 : counter - 1;

        if (min !== null && nextValue < min) nextValue = min;
        if (max !== null && nextValue > max) nextValue = max;

        if (nextValue < 0 && !allowNegative) {
            return;
        }

        setCounter(nextValue);
        onCounterChange(nextValue, action);
    };

    useEffect(() => {
        setCounter(value);
    }, [value]);

    useEffect(() => {
        if (min !== null) {
            setDisableDown(counter === min);
        } else {
            setDisableDown(false);
        }

        if (max !== null) {
            setDisableUp(counter === max);
        } else {
            setDisableUp(false);
        }
    }, [counter]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <IconButton
                onClick={onChange('subtract')}
                disabled={disabled || disableDown}
            >
                <RemoveIcon />
            </IconButton>

            <Typography
                sx={{
                    mx: 2,
                }}
            >
                {counter}
            </Typography>

            <IconButton
                onClick={onChange('add')}
                disabled={disabled || disableUp}
            >
                <AddIcon />
            </IconButton>
        </Box>
    );
};
